// ================================================================================================
// BookingSection — ส่วน "ข้อมูลนัดหมาย" ใน ProfilePanel ของ MSG (staff chat)
//
// หน้าที่:
//  1. แสดงรายการนัดหมายปัจจุบันของผู้รับบริการ (เลื่อน / ยกเลิก / ซิงก์ Google Calendar)
//  2. ฟอร์มเพิ่มนัดหมาย: เลือกสถานที่ → เลือกรับวัน (calendar) → เลือกเวลา → แพ็กเกจ/อาการ
//
// Logic หลักยกมาจาก ishealth-main (IHMobile/SelectDatetime.tsx + ServicePayment.tsx):
//  - ตารางวันว่าง/ไม่ว่าง จาก DoctorDivisionServiceBlockSchedule (DSB)
//  - เวลาว่าง (time slot) จาก timeDivision / DSB ของเซิร์ฟเวอร์เท่านั้น
//  - CardAddressInfo / CardSymptomInfo / CardAppointmentPackages (ข้างในมี LinkedPackageList ของตัวเอง)
// API handlers อยู่ใน StaffChatInterface.ts (route ใน MainHISController.tsx)
// ================================================================================================

import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { Button, Header, Icon, Label, Segment } from "semantic-ui-react";
import moment, { Moment } from "moment";

// Types
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";

// Calendar
import CustomCalendar, { CustomCalendarRef } from "react-lib/apps/common/CustomCalendar";
import ChipListItem from "react-lib/apps/IsHealth/IHMobile/ChipListItem";

// Interface (key ของ loadingStatus รายการนัดหมาย — ตั้งใน GetPatientAppointmentList)
import { PatientAppointmentListCard } from "./StaffChatInterface";

// Cards ยกมาจาก ishealth-main (IHMobile)
import CardAddressInfo from "react-lib/apps/IsHealth/IHMobile/CardAddressInfo";
import CardSymptomInfo from "react-lib/apps/IsHealth/IHMobile/CardSymptomInfo";
import CardAppointmentPackages from "react-lib/apps/IsHealth/IHMobile/CardAppointmentPackages";

// Google Calendar (ซิงก์นัดขึ้นปฏิทินของแผนก)
import {
  AppointmentCalendarAPI,
  type CalendarEventRow,
} from "react-lib/apis/issara/apps/QUE/CalendarSync";

// เวลาของ server (Asia/Bangkok) — ไม่พึ่ง timezone ของ browser
import { beDateStr, beMoment, beNowDateStr, beNowTimeStr, beStampStr, beTimeStr } from "react-lib/utils/dateUtils";

// ================================================================================================
// Constants
// ================================================================================================
const PRIMARY_COLOR = "var(--primary-theme-color)";

// Key สำหรับ loadingStatus / errorMessage (event message → card)
const ACTION = {
  loadCalendar: "CALENDAR_LOADING",
  loadTime: "TIME_SLOT_LOADING",
  loadCheckDupPatientApp: "CHECK_DUP_PATIENT_APP_LOADING",
  loadPostponeApp: "POSTPONE_PATIENT_APP_LOADING",
  loadCancelApp: "CANCEL_PATIENT_APP_LOADING",
  loadConfirmApp: "CONFIRM_PATIENT_APP_LOADING",
  loadAppointmentList: PatientAppointmentListCard,
};

// รายการว่างคงที่ — กัน props.appointments ?? [] สร้าง array ใหม่ทุก render (ทำให้ memo deps เปลี่ยน)
const NO_APPOINTMENTS: PatientAppointmentSerializer[] = [];

const PA_STATUS = {
  request: 1,
  confirm: 2,
  revise: 3,
  cancel: 5,
  reject: 6,
  abandon: 7,
  finish: 8,
};

// สถานะที่ไม่สามารถแก้ไข/ยกเลิกได้ (main: APPOINTMENT_STATUS_TERMINAL)
const APPOINTMENT_STATUS_LOCKED = [
  PA_STATUS.cancel,
  PA_STATUS.reject,
  PA_STATUS.abandon,
  PA_STATUS.finish,
];

// สถานะนัดหมาย — ใช้กับ Label ในรายการนัดหมาย
const APPOINTMENT_STATUS_LIST = [
  { id: PA_STATUS.request, value: "รอยืนยัน" },
  { id: PA_STATUS.confirm, value: "ยืนยันแล้ว" },
  { id: PA_STATUS.revise, value: "ถูกเลื่อนนัด" },
  { id: PA_STATUS.cancel, value: "ยกเลิก" },
];

// tick +1 ทุกนาที — ให้ memo ที่กรอง "วันนี้/เวลานี้" คำนวณใหม่เสมอ
// (เดิมใช้เป็น constant ตอน import → เปิดหน้าค้างข้ามบ่าย/ข้ามวัน แล้วกรองผิดทั้งวัน)
const useMinuteTick = () => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((value) => value + 1), 60 * 1000);

    return () => clearInterval(id);
  }, []);

  return tick;
};

// Inline styles (ส่วนที่ไม่ใช้ MUI)
// ชื่อวันแบบสั้น (ไทย) — ใช้กับข้อความเปิดทำการ
const DAY_NAMES_TH = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];

// แบ่งช่วงเช้า/บ่ายที่ 12:00 (ช่วงบ่าย = 12:00 ขึ้นไป)
const NOON_MINUTES = 12 * 60;

// "HH:mm" / "H:mm" → นาทีของวัน
// (เดิมเทียบ localeCompare("12:00") → "9:00" > "12:00" ตามตัวอักษร ทำให้ slot เช้า
//  ไปตกในช่วงบ่าย และ "12:00" ถูกนับเป็นเช้า) เวลาที่ parse ไม่ได้ = 0 (นับเป็นเช้า)
const timeToMinutes = (value: string) => {
  const [hours, minutes] = `${value}`.split(":").map((part) => parseInt(part, 10) || 0);
  return hours * 60 + minutes;
};

// ลายเซ็นเฉพาะวัน: id/ช่วงเวลา/status ของบล็อก DSB ของวันนั้น
// (เทียบว่า “ตารางของวันนี้เปลี่ยนจริงไหม” โดยไม่สนใจ month load ที่ไปเพิ่มข้อมูลเดือนอื่น)
const dsbDateSignature = (items: any[] | undefined, dateStr: string) =>
  (items ?? [])
    .filter((item) => beDateStr(item.start_datetime_iso) === dateStr)
    .map(
      (item) =>
        `${item.id}:${beTimeStr(item.start_datetime_iso)}-${beTimeStr(
          item.end_datetime_iso,
        )}:${item.status}:${item.only_old_patient ? 1 : 0}`,
    )
    .join("|");

// key สำหรับเรียงรายการนัด: "YYYY-MM-DD HH:mm" wall clock ของ server (Asia/Bangkok)
// - estimated_at_iso มีทั้งแบบ naive และแบบมี offset → แปลงด้วย beMoment() ไม่ให้เพี้ยนตาม TZ ของ browser
// - บาง record ไม่มี estimated_at_iso → ใช้ estimated_at ("dd/mm/yyyy [HH:mm]")
const appointmentSortStamp = (item: PatientAppointmentSerializer): string => {
  const iso = item?.estimated_at_iso;

  if (iso && beMoment(iso).isValid()) {
    return beStampStr(iso);
  }

  const match = `${item?.estimated_at ?? ""}`.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s*\[(\d{1,2}:\d{2})\])?/,
  );

  if (!match) {
    return "";
  }

  const [, day, month, year, time] = match;

  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")} ${time ?? "00:00"}`;
};

// เรียงวันเวลา เก่า → ใหม่, นัดที่ไม่มีวันเวลาไปท้ายสุด,
// id เป็นตัวเทียบคงที่ (กันลำดับสลับไปมาเมื่อ API ตอบมาไม่เหมือนเดิม / นัดเวลาเดียวกัน)
const compareAppointmentByDateTime = (
  a: PatientAppointmentSerializer,
  b: PatientAppointmentSerializer,
) => {
  const stampA = appointmentSortStamp(a);
  const stampB = appointmentSortStamp(b);
  const idA = Number(a?.id ?? 0);
  const idB = Number(b?.id ?? 0);

  if (!stampA && !stampB) {
    return idA - idB;
  }
  if (!stampA) {
    return 1;
  }
  if (!stampB) {
    return -1;
  }
  if (stampA !== stampB) {
    return stampA < stampB ? -1 : 1;
  }

  return idA - idB;
};

// รวมช่วงเวลาเปิดทำการของแต่ละวัน แล้วยุบวันต่อเนื่องที่เวลาเดียวกันเป็นช่วงเดียว
// port จาก ishealth-main: MobIsHealthProviderInterface.getOpeningHours
// (main ไม่ได้ hardcode เวลาเปิด — คำนวณจาก blocks ของตารางนัดจริง)
const formatOpeningHours = (blocks: { day: number; start: string; end: string }[]) => {
  const dayMap: Record<number, { start: string; end: string }> = {};

  for (const { day, start, end } of blocks) {
    const current = dayMap[day];

    if (current) {
      if (start < current.start) current.start = start;
      if (end > current.end) current.end = end;
    } else {
      dayMap[day] = { start, end };
    }
  }

  const days = Object.keys(dayMap)
    .map(Number)
    .sort((a, b) => a - b);

  if (!days.length) {
    return "";
  }

  const ranges: string[] = [];
  let startDay = days[0];
  let endDay = days[0];
  let time = `${dayMap[startDay].start} - ${dayMap[startDay].end}`;

  for (let i = 1; i <= days.length; i++) {
    const currentDay = days[i];
    const currentTime =
      currentDay != null ? `${dayMap[currentDay].start} - ${dayMap[currentDay].end}` : null;

    if (currentDay === endDay + 1 && currentTime === time) {
      endDay = currentDay;
      continue;
    }

    const label =
      startDay === endDay
        ? DAY_NAMES_TH[startDay]
        : `${DAY_NAMES_TH[startDay]} - ${DAY_NAMES_TH[endDay]}`;

    ranges.push(`${label} ${time}`);

    if (currentDay != null) {
      startDay = currentDay;
      endDay = currentDay;
      time = currentTime as string;
    }
  }

  return ranges.join(", ");
};

const styles = {
  sectionHeader: {
    background: PRIMARY_COLOR,
    padding: "8px 13px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: 0,
  } as CSSProperties,
  listItem: {
    padding: "10px 9px",
    position: "relative",
    margin: 0,
    border: `solid 1px ${PRIMARY_COLOR}`,
  } as CSSProperties,
  // สีสถานะนัดหมาย (Label มุมขวาบนของรายการ)
  requestStatus: {
    backgroundColor: "rgba(255, 187, 123, 0.86)",
    color: "rgba(0, 0, 0, 1)",
  } as CSSProperties,
  confirmStatus: {
    backgroundColor: "rgba(36, 197, 36, 0.3)",
    color: "rgba(0, 0, 0, 1)",
  } as CSSProperties,
  postponeStatus: {
    backgroundColor: "rgba(255, 227, 104, 0.86)",
    color: "rgba(0, 0, 0, 1)",
  } as CSSProperties,
  cancelStatus: {
    backgroundColor: "rgba(255, 4, 4, 0.08)",
    color: "rgba(255, 0, 0, 1)",
  } as CSSProperties,
};

// ================================================================================================
// Types
// ================================================================================================

type SlotItem = {
  id: string;
  status: number;
  value: string; // "HH:mm"
  original: any; // DSB item ที่เพิ่ม estimated_at_iso
};

type BookingSectionProps = {
  appointments?: PatientAppointmentSerializer[];
  isCollapsed: boolean;
  officialAccountList?: any;
  selectedChat?: any;
  onEvent?: (e: any) => any;
  setProp?: (key: string, value: any, callback?: any) => any;
  onToggle: () => void;
  onSaveAppointment?: (date: Moment, time: string, extra?: any) => void;
  onEditAppointment?: (appointment: PatientAppointmentSerializer) => void;
  onDeleteAppointment?: (appointment: PatientAppointmentSerializer) => void;
  // ---- สำหรับ logic ที่ยกมาจาก SelectDatetime.tsx (DSB schedule + time slot) ----
  timeDivision?: any[];
  createAppointment?: any;
  loadingStatus?: Record<string, any>;
  errorMessage?: Record<string, any>;
  createdAppointment?: any;
  language?: "en-US" | "th-TH";
  // ---- Google Calendar ----
  apiToken?: string;
};

// ================================================================================================
// Component
// ================================================================================================

const BookingSection: React.FC<BookingSectionProps> = (props) => {
  const calendarRef = useRef<CustomCalendarRef>();
  const minuteTick = useMinuteTick();

  // ==============================================================================================
  // 1. ข้อมูลนัดหมายของผู้รับบริการ
  // ==============================================================================================
  const appointments = props.appointments || NO_APPOINTMENTS;

  // เฉพาะสถานะที่ยังเกี่ยวข้อง (รอยืนยัน / ยืนยันแล้ว / เลื่อนนัด / ยกเลิก)
  // + เรียงตามวันเวลานัด เก่า → ใหม่ (chronological) — เดิมใช้ตามที่ API ตอบมาเลย
  // (filter() คืน array ใหม่ → sort ไม่แก้ props.appointments ต้นทาง)
  const filteredAppointments = useMemo(
    () =>
      appointments
        .filter((item) =>
          [PA_STATUS.request, PA_STATUS.confirm, PA_STATUS.revise, PA_STATUS.cancel].includes(
            item.status as number,
          ),
        )
        .sort(compareAppointmentByDateTime),
    [appointments],
  );

  // ขึ้น loading ตาม flag ของ GetPatientAppointmentList เท่านั้น
  const isLoadingAppointments = !!props.loadingStatus?.[ACTION.loadAppointmentList];

  // safety net: flag เป็น true นานผิดปกติ (request ไม่ถูกเรียก / state ถูกทับ)
  // → เปิดให้กด โหลดใหม่ ได้ ไม่ติดค้างถาวร
  const [appointmentLoadStalled, setAppointmentLoadStalled] = useState(false);

  useEffect(() => {
    if (!isLoadingAppointments) {
      setAppointmentLoadStalled(false);
      return;
    }

    const id = setTimeout(() => setAppointmentLoadStalled(true), 10 * 1000);

    return () => clearTimeout(id);
  }, [isLoadingAppointments]);

  const reloadAppointments = () => {
    const patient = props.selectedChat?.patient;
    const division = chatDivisionId;

    if (patient) {
      props.onEvent?.({
        message: "GetPatientAppointmentList",
        params: { patient, division },
      });
      return;
    }

    // ไม่มีผู้ป่วยของแชท → ปิด flag ตรง ๆ กัน spinner ค้าง
    props.setProp?.(`loadingStatus.${ACTION.loadAppointmentList}`, false);
  };

  // ==============================================================================================
  // 2. Form State — ฟอร์มเพิ่มนัดหมาย
  // ==============================================================================================
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<"morning" | "afternoon">("morning");
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  // Step ของฟอร์ม: 1 = สถานที่ + calendar + เวลา | 2 = cards (address/symptom/packages)
  const [step, setStep] = useState<1 | 2>(1);

  // โหมด "เลื่อนนัด/แก้ไขนัด" — นัดที่กำลังแก้ (null = กำลังสร้างนัดใหม่)
  const [postponeAppointment, setPostponeAppointment] = useState<any>(null);

  // นัดที่เปิดแผงยืนยันยกเลิก (port จาก MeHealthAppointment.tsx: modAction)
  const [cancelAppointment, setCancelAppointment] = useState<any>(null);
  const [cancelNote, setCancelNote] = useState<string>("");

  // นัดที่กด "ยืนยันนัด" (เก็บ id ไว้ให้ spinner ขึ้นเฉพาะแถวที่ถูกกด)
  const [confirmingAppointment, setConfirmingAppointment] = useState<any>(null);

  // ==============================================================================================
  // 3. สถานที่รับบริการ
  // ==============================================================================================
  const [showAllDivision, setShowAllDivision] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  // ค้นหาสถานที่เพิ่ม (port จาก ModSelectLocation ของ ishealth-main)
  const [locationSearch, setLocationSearch] = useState<string>("");
  // เพิ่ม key ให้ CustomCalendar remount เมื่อเปลี่ยนสถานที่ (main ใช้ handleOnLoad)
  const [calendarKey, setCalendarKey] = useState<number>(0);

  // รายการสถานที่ — จาก officialAccountList (fallback: จาก division ของนัดเดิม)
  const availableLocations = useMemo(() => {
    const oaList: any[] = props.officialAccountList ?? [];
    if (oaList.length > 0) {
      return oaList.map((oa: any) => ({
        id: oa.id,
        divisionId: oa.division,
        name: oa.division_name || oa.name || "สถานที่รับบริการ",
        address: oa.address_text || oa.address || "",
        openingHours: oa.opening_hours || oa.openingHours || "",
        officialAccount: oa,
      }));
    }

    // fallback: สร้างสถานที่จาก division ของนัดเดิม (ต้องมี id/divisionId เสมอ
    // เพราะใช้เป็น React key + เทียบว่าเลือกอยู่ + ใช้เรียก DSB)
    const locMap = new Map<string, any>();

    filteredAppointments.forEach((app: any) => {
      const name = app.division_name || app.division_location;
      const key = `${app.division ?? "division"}-${name}`;

      if (!app.division_location || locMap.has(key)) {
        return;
      }

      locMap.set(key, {
        id: app.division ?? key,
        divisionId: app.division,
        name,
        address: app.division_location,
        officialAccount: {
          division: app.division,
          name,
          division_name: app.division_name,
          address_text: app.division_location,
        },
      });
    });

    return Array.from(locMap.values());
  }, [props.officialAccountList, filteredAppointments]);

  // เลือกสถานที่เริ่มต้น — ตรงกับ division ของ chat ปัจจุบัน ถ้าไม่มีใช้ตัวแรก
  useEffect(() => {
    if (availableLocations.length > 0 && !selectedLocation) {
      const matchDivision = availableLocations.find(
        (loc) =>
          loc.divisionId === (props.selectedChat?.division_id ?? props.selectedChat?.division),
      );
      setSelectedLocation(matchDivision || availableLocations[0]);
    }
  }, [availableLocations, props.selectedChat?.division_id]);

  const activeLocation = selectedLocation || availableLocations[0];

  // Division ID ที่ใช้เรียก API — สถานที่ที่เลือก → division ของ chat → ว่าง
  const getDivisionID = () =>
    activeLocation?.divisionId ??
    props.selectedChat?.division_id ??
    props.selectedChat?.division ??
    "";

  // division ของ chat — ใช้ reload รายการนัดหลัง สร้าง/เลื่อน/ยกเลิก (ตรงกับ ProfilePanel)
  const chatDivisionId = props.selectedChat?.division_id ?? props.selectedChat?.division;

  // รายการสถานที่ที่แสดงใน picker (filter ตามคำค้น)
  const filteredLocations = availableLocations.filter((loc) => {
    const keyword = locationSearch.trim().toLowerCase();
    if (!keyword) return true;
    return `${loc?.name || ""} ${loc?.address || ""}`.toLowerCase().includes(keyword);
  });

  const handleSearchLocation = (search: string) => {
    setLocationSearch(search);
    props.onEvent?.({
      message: "GetOfficialAccountList",
      params: { search, card: "GET_LIST_OFFICIAL_ACCOUNT" },
    });
  };

  // เทียบว่าเป็นสถานที่เดียวกันหรือยัง (fallback location id ไม่จำเป็นต้องเป็น number)
  const isSameLocation = (a: any, b: any) => {
    if (!a || !b) {
      return false;
    }

    if (a.divisionId !== undefined && b.divisionId !== undefined) {
      return a.divisionId === b.divisionId;
    }

    return a.id !== undefined && a.id === b.id;
  };

  // main: handleConfirmLocation → reset วัน/เวลา + handleOnLoad ใหม่
  const handleConfirmLocation = (location: any) => {
    setShowAllDivision(false);

    if (isSameLocation(location, activeLocation)) {
      return;
    }

    setSelectedLocation(location);
    setSelectedDate(null);
    setSelectedSlotId(null);
    setSelectedPeriod("morning");
    // ล้างรายการเวลาของแผนกเก่าทันที (กันโชว์ต่อทั้งที่แผนกใหม่ไม่มีตาราง)
    props.setProp?.("timeDivision", []);
    loadedSlotScopeRef.current = "";
    resetBookingStep();
    setCalendarKey((key) => key + 1); // remount calendar → onLoad → DSB ของ division ใหม่
  };

  // สถานที่ของนัดเดิม (main: getAddressDetail(appointment)) — ใช้ตอนเลื่อนนัด
  // ถ้าแผนกนั้นอยู่ใน officialAccountList ใช้ข้อมูลนั้น ไม่งั้นสร้างจากตัวนัดเอง
  const locationFromAppointment = (appointment: any): any => {
    const match = availableLocations.find((loc) => loc?.divisionId === appointment?.division);

    if (match) {
      return match;
    }

    const oa = appointment?.extra?.official_account || {};

    return {
      id: appointment?.division ?? `appointment-${appointment?.id}`,
      divisionId: appointment?.division,
      name:
        appointment?.division_name ||
        oa?.division_name ||
        appointment?.division_location ||
        "สถานที่รับบริการ",
      address: appointment?.division_location || oa?.address_text || oa?.full_address_text || "",
      openingHours: oa?.opening_hours || "",
      officialAccount: {
        id: oa?.id,
        division: appointment?.division,
        name: oa?.name || appointment?.division_name,
        division_name: oa?.division_name || appointment?.division_name,
        address_text: oa?.address_text || appointment?.division_location,
        full_address_text: oa?.full_address_text,
        image_file: oa?.image_file,
      },
    };
  };

  // ==============================================================================================
  // 4. Calendar / Time Slot — logic จาก SelectDatetime.tsx (DSB schedule + timeDivision)
  // ==============================================================================================

  // --- 4.1 ตารางวัน: active (ว่าง) / inactive (ไม่ว่าง) จาก doctorDivisionServiceBlockSchedule
  const dsbSchedule = useMemo(() => {
    const activeSchedule: Date[] = [];
    const inactiveSchedule: Date[] = [];
    const items = props.createAppointment?.doctorDivisionServiceBlockSchedule ?? [];

    // วันนี้/ตอนนี้ ตามเวลา server (คำนวณใหม่ทุกครั้งที่ memo ทำงาน)
    const toDate = beNowDateStr();
    const toTime = beNowTimeStr();

    for (const item of items) {
      const startDate = beDateStr(item.start_datetime_iso);
      const endTime = beTimeStr(item.end_datetime_iso);

      if (startDate === toDate && endTime <= toTime) {
        continue; // วันนี้อาจผ่านเวลานั้นแล้ว → ไม่แสดง
      } else if (startDate >= toDate) {
        // สร้าง Date จาก "วัน" (wall clock) ไม่ให้เพี้ยนตาม timezone ของ browser
        const [year, month, day] = startDate.split("-").map(Number);
        const availableDate = new Date(year, month - 1, day);
        availableDate.setHours(0, 0, 0, 0);

        if (item.status === PA_STATUS.confirm || item.only_old_patient) {
          inactiveSchedule.push(availableDate); // รับเฉพาะคนไข้เก่า / ปิด → ไม่ active
        } else {
          activeSchedule.push(availableDate);
        }
      }
    }

    return { active: activeSchedule, inactive: inactiveSchedule };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.createAppointment, minuteTick]);

  // --- 4.1.1 เปิดทำการของแต่ละแผนก จากตาราง DSB ที่โหลดมาจริง
  // (แทน magic string "08:00 - 17:00" ที่เคย hardcode ไว้ทุก fallback)
  const openingHoursByDivision = useMemo(() => {
    const items = props.createAppointment?.doctorDivisionServiceBlockSchedule ?? [];
    const grouped: Record<string, { day: number; start: string; end: string }[]> = {};

    for (const item of items) {
      const divisionId = item.division ?? item.division_id;

      if (divisionId === undefined || divisionId === null) {
        continue;
      }

      const key = String(divisionId);
      const [year, month, day] = beDateStr(item.start_datetime_iso).split("-").map(Number);

      grouped[key] = grouped[key] || [];
      grouped[key].push({
        day: new Date(year, month - 1, day).getDay(),
        start: beTimeStr(item.start_datetime_iso),
        end: beTimeStr(item.end_datetime_iso),
      });
    }

    const result: Record<string, string> = {};

    Object.keys(grouped).forEach((key) => {
      result[key] = formatOpeningHours(grouped[key]);
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.createAppointment, minuteTick]);

  // เวลาเปิดทำการของสถานที่ที่เลือก: ใช้ค่าจาก API ก่อน (ถ้ามี) แล้วค่อยใช้ค่าที่คำนวณจาก DSB
  const activeOpeningHours =
    activeLocation?.openingHours ||
    openingHoursByDivision[String(activeLocation?.divisionId ?? "")] ||
    "";

  // --- 4.1.2 วันที่เลือก: มีตาราง DSB ช่วงไหนบ้าง (เช้า / บ่าย) — จากตารางของเซิร์ฟเวอร์จริง
  // ใช้ตัดสินว่า “ช่วงนั้นไม่มีอยู่จริง” แทนการเดาจาก slot ที่โหลดมา
  // (slot อาจว่างเพราะกำลังโหลด / ยังมาไม่ถึง / เต็มเป็นบางหมอ — ถ้าใช้ slot อย่างเดียว
  //  จะ disable ทั้ง 2 tab แล้วกดไปช่วงบ่ายไม่ได้)
  const periodAvailability = useMemo(() => {
    const result = { morning: false, afternoon: false };

    if (!selectedDate) {
      return result;
    }

    const dateStr = moment(selectedDate).format("YYYY-MM-DD");
    const toDate = beNowDateStr();
    const toTime = beNowTimeStr();
    const items = props.createAppointment?.doctorDivisionServiceBlockSchedule ?? [];

    for (const item of items) {
      if (beDateStr(item.start_datetime_iso) !== dateStr) {
        continue;
      }

      // ปิด / รับเฉพาะคนไข้เก่า = ใช้จองไม่ได้ (ตรงกับที่กันไว้ใน 4.1)
      if (item.status === PA_STATUS.confirm || item.only_old_patient) {
        continue;
      }

      const startMinutes = timeToMinutes(beTimeStr(item.start_datetime_iso));
      const endMinutes = timeToMinutes(beTimeStr(item.end_datetime_iso));

      // วันนี้: บล็อกที่จบไปแล้วใช้ไม่ได้
      if (dateStr === toDate && endMinutes <= timeToMinutes(toTime)) {
        continue;
      }

      // บล็อกยาวคลุมเที่ยง (เช่น 08:00-16:00) นับเป็นทั้งสองช่วง
      if (startMinutes < NOON_MINUTES) result.morning = true;
      if (endMinutes > NOON_MINUTES) result.afternoon = true;
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.createAppointment, selectedDate, minuteTick]);

  // --- 4.1.3 timeDivision ที่ใช้ได้ต้องตรงกับ “แผนก + วัน” ปัจจุบันเท่านั้น
  // timeDivision เก็บเป็น controller state เดียวทั้ง controller (ไม่แยกต่อแผนก)
  // → ถ้าเลือกสถานที่ใหม่ที่ไม่มีวันให้บริการแล้วไม่ล้างของเดิม จะยังเห็นเวลาของแผนกเก่า
  const slotScopeKey = `${activeLocation?.divisionId ?? ""}|${
    selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : ""
  }`;
  const loadedSlotScopeRef = useRef<string>("");

  // record ของ request slot ล่าสุด: ของแผนก+วันไหน + ใช้ลายเซ็นตาราง DSB ชุดไหน
  const slotRequestRef = useRef<{
    key: string;
    dateStr: string;
    signature: string;
    retried: boolean;
  } | null>(null);

  // --- 4.2 แปลง timeDivision → รายการ slot (formattedDoctorDSB)
  const formattedDoctorDSB = useMemo(() => {
    // ผลเก่า / ข้ามแผนก / ข้ามวัน = ทิ้ง (ยังไม่ได้เลือกวัน = ไม่โชว์ slot)
    if (!selectedDate || loadedSlotScopeRef.current !== slotScopeKey) {
      return [];
    }

    const flattened = (props.timeDivision ?? []).flatMap((item) => item.doctorDSB);

    return flattened.flatMap((item: any) => {
      return (item.timeValue || []).map((time: string) => {
        // วันที่ของ DSB (wall clock ของ server) + เวลาของ slot
        // → ISO พร้อม offset +07:00 เสมอ (moment().format() ใช้ TZ ของ browser
        //   ถ้าเครื่องเป็น UTC จะนัดผิดไป 7 ชั่วโมง)
        const estimatedAtIso = `${beDateStr(item.start_datetime_iso)}T${time}:00+07:00`;

        return {
          id: `${item.id}-${time}`,
          status: item.status,
          value: time,
          original: {
            ...item,
            estimated_at_iso: estimatedAtIso,
          },
        };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.timeDivision, selectedDate, slotScopeKey]);

  // --- 4.3 แยกช่วงเช้า/บ่าย + หา tab ที่ปิด (filteredPeriodDoctorDSB)
  const { morningSlots, eveningSlots, disabledPeriods } = useMemo(() => {
    const reduceTime = (formattedDoctorDSB || [])
      .filter((item) => item.status !== PA_STATUS.confirm) // status 2 = ปิด
      .reduce(
        (result: { evening: SlotItem[]; morning: SlotItem[] }, item: SlotItem) => {
          const key = timeToMinutes(item.value) >= NOON_MINUTES ? "evening" : "morning";
          result[key] = [...result[key], item];
          return result;
        },
        { evening: [], morning: [] },
      );

    // ปิด tab เฉพาะช่วงที่วันนั้น “ไม่มีตาราง” จริง
    // ช่วงที่มีตารางแต่ slot ว่างยังมาไม่ถึง (กำลังโหลด) หรือเต็มทั้งช่วง → เปิด tab ไว้
    // กันกรณี disable ทั้งเช้าและบ่ายแล้วกดช่วงบ่ายไม่ได้เลย
    const disabled: string[] = [];

    if (reduceTime.morning.length === 0 && !periodAvailability.morning) {
      disabled.push("morning");
    }
    if (reduceTime.evening.length === 0 && !periodAvailability.afternoon) {
      disabled.push("afternoon");
    }

    return {
      morningSlots: reduceTime.morning,
      eveningSlots: reduceTime.evening,
      disabledPeriods: disabled,
    };
  }, [formattedDoctorDSB, periodAvailability]);

  // --- 4.4 slot ของ tab ที่เลือก (filteredDoctorDSB)
  const filteredDoctorDSB = useMemo(() => {
    switch (selectedPeriod) {
      case "morning":
        return morningSlots;
      case "afternoon":
        return eveningSlots;
      default:
        return [];
    }
  }, [morningSlots, eveningSlots, selectedPeriod]);

  // --- 4.5 เลือกรูปแบบอัตโนมัติ (menuTabAppointmentSlot)
  useEffect(() => {
    const morningCount = morningSlots.length;
    const eveningCount = eveningSlots.length;
    const currentEmpty = selectedPeriod === "morning" ? morningCount === 0 : eveningCount === 0;
    const otherPeriod: "morning" | "afternoon" =
      selectedPeriod === "morning" ? "afternoon" : "morning";
    const otherHasSlot = otherPeriod === "morning" ? morningCount > 0 : eveningCount > 0;

    // ช่วงที่เปิดอยู่ไม่มีเวลาว่าง แต่อีกช่วงมี → ย้ายไปช่วงที่มีให้อัตโนมัติ
    // (เดิมข้ามกรณี disable ทั้งคู่ → วันที่มีแต่เวลาบ่ายค้างอยู่ที่ tab เช้า)
    if (currentEmpty && otherHasSlot) {
      setSelectedPeriod(otherPeriod);
      return;
    }

    // ยังไม่มี slot จากเซิร์ฟเวอร์ (กำลังโหลด) → ใช้ตาราง DSB ของวันนำหน้า
    // วันที่มีแต่ช่วงบ่าย ควรเปิด tab บ่ายไว้เลย ไม่ค้างที่ช่วงเช้า
    if (morningCount === 0 && eveningCount === 0 && selectedDate) {
      if (!periodAvailability.morning && periodAvailability.afternoon) {
        setSelectedPeriod("afternoon");
      } else if (periodAvailability.morning && !periodAvailability.afternoon) {
        setSelectedPeriod("morning");
      }
    }
  }, [morningSlots, eveningSlots, periodAvailability, selectedPeriod, selectedDate]);

  const selectedSlot = useMemo(
    () => formattedDoctorDSB.find((item) => item.id === selectedSlotId) || null,
    [formattedDoctorDSB, selectedSlotId],
  );

  // เวลาที่เลือกได้มีที่มาเดียวคือ slot ของเซิร์ฟเวอร์ ใช้ DSB/timeDivision
  const effectiveSelectedTime = selectedSlot?.value ?? "";

  const isLoadingTime = !!props.loadingStatus?.[ACTION.loadTime];
  const isLoadingCalendar = !!props.loadingStatus?.[ACTION.loadCalendar];

  // Step 1 ครบ (วัน + slot จากเซิร์ฟเวอร์) → ไป step 2 อัตโนมัติ *เฉพาะเมื่อการเลือกเปลี่ยน*
  // (เดิมเทียบแค่ว่า "ครบอยู่" → กด "แก้สถานที่ / วัน / เวลา" แล้ว effect ยัดกลับ
  //  step 2 ทันที ต้องกดซ้ำหลายครั้งถึงจะย้อนกลับได้จริง)
  const step1Signature = [
    selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : "",
    selectedSlotId || "",
  ].join("|");

  const autoAdvancedSignatureRef = useRef<string | null>(null);

  // ทุกครั้งที่เริ่มการเลือกชุดใหม่ (เปิดฟอร์ม / เปลี่ยนสถานที่ / โหมดเลื่อนนัด / สร้างเสร็จ)
  // ต้องล้างร่องรอยเดิม เพื่อให้ auto-advance ทำงานได้อีก
  const resetBookingStep = () => {
    autoAdvancedSignatureRef.current = null;
    setStep(1);
  };

  useEffect(() => {
    if (!selectedDate || !effectiveSelectedTime) {
      autoAdvancedSignatureRef.current = null;
      return;
    }

    if (step === 1 && autoAdvancedSignatureRef.current !== step1Signature) {
      autoAdvancedSignatureRef.current = step1Signature;
      setStep(2);
    }
  }, [step, step1Signature, selectedDate, effectiveSelectedTime]);

  // ==============================================================================================
  // 5. Appointment Packages + locState (สำหรับ Card* components)
  // ==============================================================================================

  // แพ็กเกจที่เลือกผูกกับนัดหมาย
  const [appointmentPackages, setAppointmentPackages] = useState<any[]>([]);
  // บันทึกการนัด (order note) + ใช้เป็น repetition_note กรณีมีอีกนัดภายใน 3 ชม.
  const [orderNote, setOrderNote] = useState<string>("");

  const isReadOnly = false;
  // main: เลือกแพ็กเกจได้เฉพาะตอนทำนัดใหม่ (isPostponeApp = แก้ไขนัดเดิม)
  const canSelectPackages = !postponeAppointment;

  const handleEditInfo = () => {};
  const handleChangePackages = (packages: any[]) => setAppointmentPackages(packages);

  // locState — ข้อมูลสถานที่/วันเวลาของนัด (pattern เดิมของ main อ่านจาก router state)
  // ใน MSG ส่งเป็น prop locationState ให้ CardAddressInfo ตรง ๆ
  // (เดิมเรียก setLocationState() ใน body ของ render ซึ่งเป็น side effect ระหว่าง render)
  const locState = {
    slot: {
      estimated_at_iso:
        selectedDate && effectiveSelectedTime
          ? `${moment(selectedDate).format("YYYY-MM-DD")}T${effectiveSelectedTime}:00`
          : undefined,
      appointment_id: null,
    },
    appointment: postponeAppointment || {},
    basicSymptomInfo: undefined,
    appointmentOrderNote: orderNote,
    isPostponeApp: !!postponeAppointment,
    appointmentPackages,
    officialAccount: activeLocation?.officialAccount || {},
    division_name: activeLocation?.name || "",
    division_location: activeLocation?.address || "",
  };

  // ==============================================================================================
  // 6. Google Calendar — ซิงก์นัดขึ้นปฏิทินของแผนก
  // ==============================================================================================

  const [calendarEventMap, setCalendarEventMap] = useState<Record<string, CalendarEventRow>>({});
  const [calendarBusy, setCalendarBusy] = useState<Record<string, boolean>>({});
  const [calendarNotice, setCalendarNotice] = useState<string | null>(null);

  const appointmentIdKey = filteredAppointments.map((item) => item.id).join(",");

  // โหลดสถานะ event (synced / html_link) ของนัดทั้งหมด
  useEffect(() => {
    if (!props.apiToken || !appointmentIdKey) {
      setCalendarEventMap({});
      return;
    }
    let cancelled = false;
    AppointmentCalendarAPI.eventsFor({
      appointmentIds: appointmentIdKey.split(","),
      apiToken: props.apiToken,
    })
      .then(({ results }) => {
        if (cancelled) return;
        const map: Record<string, CalendarEventRow> = {};
        results.forEach((row) => {
          // เอารายการของ MeHealth เป็นหลัก (มี event ของผู้ป่วยด้วยในนัดเดียวกัน)
          if (!map[row.patient_appointment] || row.side.startsWith("MeHealth")) {
            map[row.patient_appointment] = row;
          }
        });
        setCalendarEventMap(map);
      })
      .catch(() => {
        if (!cancelled) setCalendarEventMap({});
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentIdKey, props.apiToken]);

  // ซิงก์นัดเดียว → สร้าง/อัปเดต event + ส่งคำเชิญไปหาผู้รับบริการ
  const syncAppointmentToCalendar = async (appointment: PatientAppointmentSerializer) => {
    setCalendarBusy((prev) => ({ ...prev, [appointment.id]: true }));
    setCalendarNotice(null);
    try {
      const result = await AppointmentCalendarAPI.sync({
        appointmentId: appointment.id,
        apiToken: props.apiToken,
        notifyPatient: true,
        force: true,
      });
      const warning = result.warnings?.[0];
      if (warning) setCalendarNotice(warning);
      const { results } = await AppointmentCalendarAPI.eventsFor({
        appointmentIds: [appointment.id],
        apiToken: props.apiToken,
      });
      setCalendarEventMap((prev) => ({
        ...prev,
        [appointment.id]: results.find((row) => row.side.startsWith("MeHealth")) || results[0],
      }));
    } catch (e: any) {
      setCalendarNotice(e?.response?.data?.detail || e?.message || "ซิงก์ไม่สำเร็จ");
    } finally {
      setCalendarBusy((prev) => ({ ...prev, [appointment.id]: false }));
    }
  };

  // ==============================================================================================
  // 7. Event Handlers
  // ==============================================================================================

  // --- 7.1 ฟอร์ม ---

  const handleToggleAddForm = () => {
    if (!showAddForm) {
      if (props.isCollapsed) {
        props.onToggle();
      }
      // รีเซ็ตฟอร์มทุกครั้งก่อนเปิด (DSB schedule โหลดผ่าน preload effect + CustomCalendar onLoad)
      setSelectedDate(null);
      setSelectedPeriod("morning");
      setSelectedSlotId(null);
      props.setProp?.("timeDivision", []);
      loadedSlotScopeRef.current = "";
      setShowAllDivision(false);
      setOrderNote("");
      resetBookingStep();
      setPostponeAppointment(null);
    } else {
      // ปิดฟอร์ม ← ออกทุกโหมด (รวมโหมดเลื่อนนัด)
      setPostponeAppointment(null);
      setCancelAppointment(null);
    }
    setShowAddForm(!showAddForm);
  };

  // --- 7.2 DSB schedule (calendar) ---

  // Preload ตารางนัดเดือนปัจจุบันทันทีที่เข้าสู่ section
  // (เดิมโหลดเฉพาะตอนเปิดฟอร์มผ่าน CustomCalendar onLoad)
  useEffect(() => {
    props.onEvent?.({
      message: "HandleDoctorDivisionServiceBlockSchedule",
      params: {
        divisionID: getDivisionID(),
        doctorID: "",
        month: moment().format("M"),
        year: moment().format("YYYY"),
        is_telemed: false,
        card: ACTION.loadCalendar,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocation?.divisionId, props.selectedChat?.division_id]);

  // เลื่อน calendar ไปอีกเดือน/ปี (handleChangeMonthYear)
  const handleChangeMonthYear = (dateRange: any[]) => {
    const prevMonth = moment(dateRange?.[0]?.start_date);
    props.onEvent?.({
      message: "HandleDoctorDivisionServiceBlockScheduleNextMonthYear",
      params: {
        divisionID: getDivisionID(),
        doctorID: "",
        month: prevMonth.format("M"),
        year: prevMonth.format("YYYY"),
        is_telemed: false,
        card: ACTION.loadCalendar,
      },
    });
  };

  // CustomCalendar request ตารางเดือนที่เลือก (handleDoctorDSBSchedule)
  const handleDoctorDSBSchedule = (dateRange: any[]) => {
    const requestedDate = moment(dateRange?.[0]?.start_date || "");
    props.onEvent?.({
      message: "HandleDoctorDivisionServiceBlockSchedule",
      params: {
        divisionID: getDivisionID(),
        doctorID: "",
        month: requestedDate.format("M"),
        year: requestedDate.format("YYYY"),
        is_telemed: false,
        card: ACTION.loadCalendar,
      },
    });
  };

  // Initial load ของ CustomCalendar (handleOnLoad)
  const handleOnLoad = () => {
    calendarRef.current?.getCalendar({
      isTelemed: false,
      appointmentType: "check_up",
    });
  };

  // --- 7.3 วัน/เวลา ---

  const requestTimeSlots = (dateStr: string) => {
    const key = `${activeLocation?.divisionId ?? ""}|${dateStr}`;

    // จำว่า slot ที่กำลังขอ เป็นของแผนก+วันไหน (ใช้เทียบใน 4.1.3)
    loadedSlotScopeRef.current = key;
    slotRequestRef.current = {
      key,
      dateStr,
      signature: dsbDateSignature(
        props.createAppointment?.doctorDivisionServiceBlockSchedule,
        dateStr,
      ),
      retried: false,
    };

    props.setProp?.("timeDivision", []); // ล้างของเก่าก่อน request ใหม่

    props.onEvent?.({
      message: "HandleGetTimeSlotByDate",
      params: {
        selectDate: dateStr,
        // divisionID ให้ handler เทียบว่า request ไหนเป็นล่าสุด (สลับแผนกด้วยวันเดียวกัน)
        divisionID: activeLocation?.divisionId ?? "",
        card: ACTION.loadTime,
      },
    });
  };

  // เลือกวัน (handleDateChange) → ขอ time slot ของวันนั้น
  const handleDateChange = (date: Moment | null) => {
    const dateStr = date ? moment(date).format("YYYY-MM-DD") : "";

    setSelectedDate(date);
    setSelectedSlotId(null);

    if (date) {
      requestTimeSlots(dateStr);
      return;
    }

    // ไม่เลือกวัน → ไม่ต้องขอ slot แต่ต้องล้างของเก่า/record เดิม
    loadedSlotScopeRef.current = "";
    slotRequestRef.current = null;
    props.setProp?.("timeDivision", []);
  };

  // --- 7.3.1 ปฏิทินโหลดตาราง DSB ของวันนั้นมา “ทีหลัง” request slot
  // (เช่นกดวันทันทีตอน CALENDAR_LOADING ยังไม่จบ — slot ถูกคำนวณจากตารางที่ยังไม่ครบ
  // หรือถูก handler ทิ้งเพราะตารางถูกเขียนทับระหว่างรอ → วันนั้นมีบ่ายให้เลือกแต่ UI ไม่ขึ้น)
  // → เจอว่าลายเซ็นของวันนี้เปลี่ยนหลัง request ล่าสุด → ขอใหม่ 1 ครั้งต่อ 1 วัน/แผนก (กัน loop)
  useEffect(() => {
    const last = slotRequestRef.current;

    if (!selectedDate || !last || last.retried) {
      return;
    }

    const dateStr = moment(selectedDate).format("YYYY-MM-DD");
    const key = `${activeLocation?.divisionId ?? ""}|${dateStr}`;

    if (last.key !== key || last.dateStr !== dateStr) {
      return;
    }

    const signature = dsbDateSignature(
      props.createAppointment?.doctorDivisionServiceBlockSchedule,
      dateStr,
    );

    // ตารางของวันนี้ยังเหมือนเดิมกับที่ใช้ request → ผลที่ถืออยู่ใช้ได้แล้ว
    if (signature === last.signature) {
      return;
    }

    slotRequestRef.current = { ...last, signature, retried: true };
    requestTimeSlots(last.dateStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.createAppointment, selectedDate, activeLocation?.divisionId]);

  // เลือกช่วงเวลาเช้า/บ่าย
  const handlePeriodChange = (period: "morning" | "afternoon") => {
    if (disabledPeriods.includes(period)) {
      return;
    }
    setSelectedPeriod(period);
    setSelectedSlotId(null);
  };

  // --- 7.4 ยืนยันสร้างนัดหมาย ---

  // ฟอร์มกำลังสร้างนัดใหม่ หรือกำลังเลื่อนนัดเดิม → ใช้ card คนละตัวกัน
  const activeFormCard = postponeAppointment
    ? ACTION.loadPostponeApp
    : ACTION.loadCheckDupPatientApp;

  const isSubmitting = !!props.loadingStatus?.[activeFormCard];
  const formError = props.errorMessage?.[activeFormCard];
  const cancelError = props.errorMessage?.[ACTION.loadCancelApp];
  const confirmError = props.errorMessage?.[ACTION.loadConfirmApp];

  // clear errorMessage ของ card หนึ่ง (SetPropSeparate รองรับ key แบบ dot-path)
  const clearCardError = (card: string) => {
    props.setProp?.(`errorMessage.${card}`, null);
  };

  const closeFormError = () => {
    [ACTION.loadCheckDupPatientApp, ACTION.loadPostponeApp].forEach(clearCardError);
  };

  // ลำดับเหมือน main: ตรวจนัดซ้ำก่อน — handler ของ MSG จะสร้างนัดต่อให้อัตโนมัติ
  const handleSubmitAppointment = () => {
    const data = selectedSlot?.original;

    if (!data) {
      return;
    }

    closeFormError();

    // โหมดแก้ไขนัดเดิม → PATCH (HandlePostponePatientAppointment) เหมือน main ที่ไม่ตรวจนัดซ้ำ
    if (postponeAppointment) {
      handlePostponeAppointment(data);
      return;
    }

    // ถ้า caller เตรียม onSaveAppointment ไว้ ให้ใช้ของ caller แทน
    if (props.onSaveAppointment) {
      props.onSaveAppointment(
        moment((selectedDate || data.estimated_at_iso) as any),
        effectiveSelectedTime,
        {
          slot: data,
          officialAccount: activeLocation?.officialAccount,
          appointmentPackages,
        },
      );
      return;
    }

    props.onEvent?.({
      message: "HandleCheckDuplicatePatientApp",
      params: {
        data,
        card: ACTION.loadCheckDupPatientApp,
        officialAccount: activeLocation?.officialAccount,
        appointmentPackages,
        appointmentOrderNote: locState.appointmentOrderNote,
        repetitionNote: orderNote,
        basicSymptomInfo: locState.basicSymptomInfo,
        refreshDivision: chatDivisionId,
      },
    });
  };

  // --- 7.5 แก้ไข / ยกเลิกนัดในรายการ (port จาก MeHealthAppointment.tsx: handleAction) ---

  // นัดที่ยังจัดการได้ — main กันสถานะ 5/6/7/8 ไว้ (APPOINTMENT_STATUS_TERMINAL)
  const canManageAppointment = (appointment: any) =>
    !APPOINTMENT_STATUS_LOCKED.includes(appointment?.status as number);

  // params ชุดเดียวกันของ เลื่อน / ยกเลิก / ยืนยัน (PATCH นัดเดิม)
  const buildAppointmentActionParams = (appointment: any, card: string) => ({
    id: appointment?.id,
    appointment,
    patient: appointment?.patient ?? props.selectedChat?.patient,
    division: appointment?.division,
    division_service_block:
      appointment?.division_service_block ?? appointment?.division_service_block_id,
    card,
    refreshDivision: chatDivisionId,
  });

  // "แก้ไข/เลื่อนนัด" → เปิดฟอร์มในโหมดแก้ไข เริ่มที่ step 1 (สถานที่ + วัน/เวลาใหม่)
  const handleStartPostpone = (appointment: any) => {
    if (props.isCollapsed) {
      props.onToggle();
    }

    setPostponeAppointment(appointment);
    setCancelAppointment(null);
    setShowAddForm(true);
    resetBookingStep();
    setShowAllDivision(false);
    setSelectedDate(null);
    setSelectedSlotId(null);
    setSelectedPeriod("morning");
    setAppointmentPackages([]);
    setOrderNote(appointment?.order_note || "");

    // ใช้สถานที่/แผนกของนัดเดิม → remount calendar ให้โหลด DSB ของแผนกนั้น
    setSelectedLocation(locationFromAppointment(appointment));
    setCalendarKey((key) => key + 1);
    closeFormError();
  };

  // ออกจากโหมดแก้ไข โดยไม่เปลี่ยนนัด
  const handleCancelPostpone = () => {
    setPostponeAppointment(null);
    setShowAddForm(false);
    resetBookingStep();
    setSelectedDate(null);
    setSelectedSlotId(null);
    setSelectedPeriod("morning");
    setOrderNote("");
    setAppointmentPackages([]);
    closeFormError();
  };

  // submit ของโหมดแก้ไข: PATCH นัดเดิมด้วย slot ที่เลือกใหม่
  const handlePostponeAppointment = (slot: any) => {
    const appointment = postponeAppointment;

    if (!appointment) {
      return;
    }

    props.onEvent?.({
      message: "HandlePostponePatientAppointment",
      params: {
        ...buildAppointmentActionParams(appointment, ACTION.loadPostponeApp),
        slot,
        note: orderNote,
        orderNote,
        // คงสถานะเดิมไว้ (รอยืนยัน/ยืนยันแล้ว) — เลื่อนเฉพาะวัน/เวลา
        status: appointment.status,
        isChangeProvider: false,
      },
    });
  };

  // "ยกเลิกนัด" → แผงยืนยันพร้อมเหตุผล (main ไปหน้า /cancel-appointment/ ซึ่งมีช่อง note)
  const handleRequestCancel = (appointment: any) => {
    setCancelAppointment(appointment);
    setCancelNote("");
    clearCardError(ACTION.loadCancelApp);
  };

  const handleConfirmCancel = (appointment: any) => {
    setCancelAppointment(null);
    setCancelNote("");

    props.onEvent?.({
      message: "HandleCancelPatientAppointment",
      params: {
        ...buildAppointmentActionParams(appointment, ACTION.loadCancelApp),
        note: cancelNote,
      },
    });
  };

  const canConfirmAppointment = (appointment: any) =>
    [PA_STATUS.request, PA_STATUS.revise].includes(appointment?.status as number);

  const handleConfirmAppointment = (appointment: any) => {
    setConfirmingAppointment(appointment);

    props.onEvent?.({
      message: "HandleConfirmPatientAppointment",
      params: {
        ...buildAppointmentActionParams(appointment, ACTION.loadConfirmApp),
      },
    });
  };

  const clearConfirmError = () => clearCardError(ACTION.loadConfirmApp);

  // สร้าง/เลื่อนนัดสำเร็จ (createdAppointment ใหม่จาก controller) → ปิดฟอร์ม + reset
  const lastCreatedRef = useRef<any>(null);
  useEffect(() => {
    const created = props.createdAppointment;

    if (!created || created === lastCreatedRef.current) {
      return;
    }

    lastCreatedRef.current = created;
    setShowAddForm(false);
    resetBookingStep();
    setPostponeAppointment(null);
    setSelectedDate(null);
    setSelectedSlotId(null);
    setOrderNote("");
    setAppointmentPackages([]);
  }, [props.createdAppointment]);

  // ==============================================================================================
  // 8. Helpers — สำหรับรายการนัดหมาย
  // ==============================================================================================

  // แยก "วันที่ [เวลา]" เช่น "14 ก.พ. 2569 [09:30]" → { date, time }
  const splitDateTime = (input?: string | null): { date: string; time: string } => {
    if (!input) return { date: "", time: "" };

    const match = input.match(/^(\d{1,2}\/\d{1,2}\/\d{4})\s*\[(\d{1,2}:\d{2})\]$/);
    if (!match) {
      return { date: input.trim(), time: "" };
    }

    const [, date, time] = match;
    return { date, time };
  };

  // Label + สีของสถานะนัดหมาย
  const getStatusDisplay = (status?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8) => {
    if (!status) {
      return { text: "", style: {} };
    }

    const statusMap: Record<number, CSSProperties> = {
      1: styles.requestStatus,
      2: styles.confirmStatus,
      3: styles.postponeStatus,
      5: styles.cancelStatus,
    };

    const item = APPOINTMENT_STATUS_LIST.find((s) => s.id === status);

    return {
      text: item?.value || "",
      style: statusMap[status] || {},
    };
  };

  // ==============================================================================================
  // 9. Render
  // ==============================================================================================
  return (
    <Segment.Group style={{ marginBottom: "10px" }}>
      {/* ---------- Header (click เพื่อ collapse/expand) ---------- */}
      <Segment inverted style={styles.sectionHeader} onClick={props.onToggle}>
        <Header as="h5" inverted style={{ margin: 0, fontSize: "13px" }}>
          ข้อมูลนัดหมาย
        </Header>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button
            size="mini"
            style={{
              padding: "3px 8px",
              margin: "0 10px 0 0",
              background: showAddForm ? "#FFF0F3" : "white",
              color: PRIMARY_COLOR,
              fontWeight: 600,
              borderRadius: "4px",
              fontSize: "11px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleAddForm();
            }}
          >
            <Icon name={showAddForm ? "close" : "plus"} style={{ margin: "0 4px 0 0" }} />
            {showAddForm ? "ปิดฟอร์ม" : "เพิ่มนัดหมาย"}
          </Button>
          <Icon name={props.isCollapsed ? "angle down" : "angle up"} style={{ margin: 0 }} />
        </div>
      </Segment>

      {!props.isCollapsed && (
        <>
          {/* ---------- ฟอร์มเพิ่มนัดหมาย ---------- */}
          {showAddForm && (
            <Segment
              style={{
                padding: "10px 8px",
                margin: 0,
                border: `solid 1px ${PRIMARY_COLOR}`,
                borderTop: "none",
                borderRadius: filteredAppointments.length === 0 ? "0 0 5px 5px" : 0,
                backgroundColor: "#FFFFFF",
              }}
            >
              {/* ----- 9.0 โหมดแก้ไขนัด (เลื่อนนัดหมาย) ----- */}
              {postponeAppointment && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "6px",
                    border: "1px solid #F5CAD5",
                    borderLeft: "4px solid #f2711c",
                    borderRadius: "6px",
                    background: "#FFF8FA",
                    padding: "7px 9px",
                    marginBottom: "10px",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#f2711c" }}>
                      <Icon name="calendar minus outline" style={{ margin: "0 4px 0 0" }} />
                      กำลังเลื่อนนัดหมาย
                    </div>
                    <div style={{ fontSize: "10px", color: "#777777", marginTop: "2px" }}>
                      นัดเดิม: {splitDateTime(postponeAppointment.estimated_at).date || "-"}{" "}
                      {splitDateTime(postponeAppointment.estimated_at).time || "-"}
                    </div>
                    <div style={{ fontSize: "10px", color: "#777777", marginTop: "2px" }}>
                      เลือกสถานที่ / วัน / เวลาใหม่ แล้วกดยืนยันด้านล่าง
                    </div>
                  </div>
                  <Button
                    size="mini"
                    basic
                    color="orange"
                    style={{
                      fontSize: "10px",
                      padding: "3px 7px",
                      margin: 0,
                      borderRadius: "5px",
                    }}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      handleCancelPostpone();
                    }}
                  >
                    ยกเลิกการแก้ไข
                  </Button>
                </div>
              )}

              {/* ----- 9.1-9.3 Step 1: สถานที่ + calendar + เวลา ----- */}
              {step === 1 && (
                <>
                  <div
                    style={{
                      border: "1px solid #ECECEC",
                      borderRadius: "10px",
                      padding: "8px 10px",
                      backgroundColor: "#FFF8FA",
                      marginBottom: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowAllDivision(!showAllDivision)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <Icon
                          name="map marker alternate"
                          style={{ color: PRIMARY_COLOR, margin: 0 }}
                        />
                        <span style={{ fontSize: "11px", fontWeight: 700, color: "#333333" }}>
                          สถานที่รับบริการ
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "10px",
                          color: PRIMARY_COLOR,
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <span>{showAllDivision ? "ย่อ" : "ดูเพิ่มเติม"}</span>
                        <Icon
                          name={showAllDivision ? "chevron up" : "chevron down"}
                          style={{ marginLeft: "3px" }}
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#4A4A4A",
                        marginTop: "4px",
                      }}
                    >
                      {activeLocation?.name}
                    </div>

                    {activeLocation?.address && (
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#777777",
                          marginTop: "2px",
                          whiteSpace: showAllDivision ? "pre-wrap" : "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {activeLocation?.address}
                      </div>
                    )}

                    {/* เปิดทำการจริงของแผนก (คำนวณจากตาราง DSB) — ไม่แสดงถ้าไม่มีข้อมูล */}
                    {activeOpeningHours && (
                      <div style={{ fontSize: "10px", color: "#777777", marginTop: "2px" }}>
                        เปิดทำการ: {activeOpeningHours}
                      </div>
                    )}

                    {/* ----- 9.1.1 Location picker (port จาก ModSelectLocation) ----- */}
                    {showAllDivision && (
                      <div style={{ marginTop: "8px" }}>
                        <div style={{ display: "flex", gap: "5px" }}>
                          <input
                            value={locationSearch}
                            placeholder="ค้นหาสถานที่รับบริการ"
                            onChange={(e) => setLocationSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleSearchLocation(locationSearch);
                              }
                            }}
                            style={{
                              flex: 1,
                              fontSize: "11px",
                              padding: "5px 7px",
                              border: `1px solid ${PRIMARY_COLOR}`,
                              borderRadius: "5px",
                              outline: "none",
                            }}
                          />
                          <Button
                            size="mini"
                            style={{
                              background: PRIMARY_COLOR,
                              color: "#FFFFFF",
                              fontSize: "10px",
                              margin: 0,
                              borderRadius: "5px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSearchLocation(locationSearch);
                            }}
                          >
                            ค้นหา
                          </Button>
                        </div>

                        <div
                          style={{
                            maxHeight: "170px",
                            overflowY: "auto",
                            marginTop: "6px",
                            border: "1px solid #F3E0E6",
                            borderRadius: "6px",
                          }}
                        >
                          {filteredLocations.map((loc) => {
                            const isSelected = isSameLocation(loc, activeLocation);
                            return (
                              <div
                                key={loc?.id}
                                onClick={() => handleConfirmLocation(loc)}
                                style={{
                                  padding: "6px 8px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #F6E6EC",
                                  backgroundColor: isSelected ? "#FFF0F3" : "#FFFFFF",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: isSelected ? 700 : 500,
                                    color: isSelected ? PRIMARY_COLOR : "#4A4A4A",
                                  }}
                                >
                                  {loc?.name}
                                </div>
                                {loc?.address && (
                                  <div
                                    style={{ fontSize: "10px", color: "#777777", marginTop: "2px" }}
                                  >
                                    {loc.address}
                                  </div>
                                )}
                                {loc.openingHours ||
                                openingHoursByDivision[String(loc?.divisionId ?? "")] ? (
                                  <div
                                    style={{ fontSize: "10px", color: "#999999", marginTop: "2px" }}
                                  >
                                    เปิดทำการ:{" "}
                                    {loc.openingHours ||
                                      openingHoursByDivision[String(loc?.divisionId ?? "")]}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}

                          {filteredLocations.length === 0 && (
                            <div style={{ padding: "8px", fontSize: "10px", color: "#999999" }}>
                              ไม่พบสถานที่รับบริการ
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ----- 9.2 Calendar ----- */}
                  {/* Override calendar.scss ที่บังคับ .rc_root .rc เป็น 90vw/91vw (ออกแบบมาสำหรับ full-page)
                  ให้ calendar กว้างตาม panel — ต้องรักษาสัดส่วน 1:1 (สี่เหลี่ยม)
                  เพราะ row ใช้ height เป็น % ของ container */}
                  <style>{`
                /* CustomCalendar root + div ห่อ calendar (div ที่ไม่มี class ของ lib)
                   ต้องเป็น block + เต็มกว้างเสมอ เพราะ flex + shrink-to-fit ทำให้
                   .rc_root (width:100%) ย่อเหลือขนาด content ของ calendar เอง */
                .msg-booking-calendar > div:not([class]) {
                  display: block !important;
                  width: 100% !important;
                }
                .msg-booking-calendar > div:not([class]) > div:not([class]) {
                  display: block !important;
                  width: 100% !important;
                }
                .msg-booking-calendar .rc_root {
                  width: 100% !important;
                  box-sizing: border-box;
                }
                .msg-booking-calendar .rc_root .rc {
                  width: 100% !important;
                  height: auto !important;
                  aspect-ratio: 1 / 1;
                }
              `}</style>
                  <div
                    className="msg-booking-calendar"
                    style={{
                      border: "1px solid #ECECEC",
                      borderRadius: "14px",
                      padding: "10px 8px",
                      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)",
                      backgroundColor: "#FFFFFF",
                      // กว้างคงที่เสมอ (panel 325 → ไม่เกิน 285)
                      // ไม่งั้น calendar จะย่อตาม max-content ของตัวเอง (เห็นเป็น “หดเล็ก”)
                      width: "100%",
                      maxWidth: "285px",
                      boxSizing: "border-box",
                      overflow: "hidden",
                    }}
                  >
                    <CustomCalendar
                      key={calendarKey}
                      ref={calendarRef}
                      // panel ProfilePanel กว้าง 325px → calendar ~285px (ไม่ใช้ window.innerWidth)
                      calendarSize={285}
                      language={props.language || "th-TH"}
                      value={selectedDate ? moment(selectedDate) : null}
                      defaultValue={selectedDate ? moment(selectedDate) : moment()}
                      calendarList={{
                        active: dsbSchedule.active,
                        inactive: dsbSchedule.inactive,
                      }}
                      isLoadingCalendar={isLoadingCalendar}
                      isLoadingNextMonth={isLoadingCalendar}
                      onChange={handleDateChange}
                      onNextMonth={handleChangeMonthYear}
                      onGetCalendar={handleDoctorDSBSchedule}
                      onLoad={handleOnLoad}
                    />
                  </div>

                  {/* ----- 9.3 เลือกเวลา ----- */}
                  <div style={{ marginTop: "12px" }}>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#333333",
                        marginBottom: "8px",
                      }}
                    >
                      เลือกช่วงเวลาที่สะดวกเข้ารับบริการ
                      <span style={{ color: "#656565", fontWeight: 400 }}>
                        (โปรดเลือกเวลาที่ต้องการ)
                      </span>
                    </div>

                    {/* Tab ช่วงเช้า/บ่าย — disable tab ที่ไม่มี slot */}
                    <div
                      style={{
                        display: "flex",
                        backgroundColor: "#FFF5F7",
                        borderRadius: "8px",
                        padding: "2px",
                        marginBottom: "10px",
                      }}
                    >
                      {(["morning", "afternoon"] as const).map((period) => {
                        const isDisabled = disabledPeriods.includes(period);
                        const isActive = selectedPeriod === period;
                        return (
                          <button
                            key={period}
                            type="button"
                            onClick={() => handlePeriodChange(period)}
                            style={{
                              flex: 1,
                              padding: "6px 0",
                              borderRadius: "6px",
                              border: "none",
                              fontSize: "11px",
                              fontWeight: 600,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              opacity: isDisabled ? 0.5 : 1,
                              backgroundColor: isActive ? PRIMARY_COLOR : "transparent",
                              color: isActive ? "#FFFFFF" : PRIMARY_COLOR,
                              transition: "all 0.2s ease",
                            }}
                          >
                            {period === "morning" ? "ช่วงเช้า" : "ช่วงบ่าย"}
                          </button>
                        );
                      })}
                    </div>

                    {/* Time slot (DSB/timeDivision) */}
                    <div style={{ marginBottom: "12px" }}>
                      <ChipListItem
                        items={filteredDoctorDSB}
                        minWidth={90}
                        highlight
                        loading={isLoadingTime}
                        selected={selectedSlotId}
                        onSelect={(data: any) => setSelectedSlotId(data.id)}
                      />

                      {!isLoadingTime && filteredDoctorDSB.length === 0 && (
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#8a6d3b",
                            background: "#FCF8E3",
                            border: "1px solid #FAEBCB",
                            borderRadius: "5px",
                            padding: "6px 8px",
                          }}
                        >
                          <Icon name="warning" style={{ margin: "0 4px 0 0" }} />
                          {!selectedDate
                            ? "แผนกนี้ยังไม่มีวันที่เลือกได้ในปฏิทิน (ไม่มีตารางนัด DSB จากเซิร์ฟเวอร์) — ลองดูเดือนถัดไปหรือเลือกแผนกอื่น"
                            : periodAvailability[selectedPeriod]
                              ? `ช่วง${
                                  selectedPeriod === "morning" ? "เช้า" : "บ่าย"
                                }ของวันนี้ถูกเต็ม/ปิดหมดแล้ว — ลองเลือกอีกช่วงหรือวันอื่น`
                              : "ไม่พบตารางเวลาของแผนกในช่วงเวลานี้ (DSB จากเซิร์ฟเวอร์) — ลองเลือกวันอื่น หรือดูเดือนถัดไป"}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ย้อนกลับมา step 1 แล้วยังไม่เปลี่ยนวัน/เวลา → มีปุ่มไปต่อให้
                      (auto-advance จำ signature เดิมไว้ จะไม่พาไปเอง) */}
                  {!!selectedDate && !!effectiveSelectedTime && (
                    <Button
                      fluid
                      primary
                      style={{
                        background: "transparent",
                        color: PRIMARY_COLOR,
                        borderColor: PRIMARY_COLOR,
                        boxShadow: `0 0 0 1px ${PRIMARY_COLOR} inset`,
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "8px 0",
                        marginTop: "4px",
                      }}
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setStep(2);
                      }}
                    >
                      <Icon name="arrow right" style={{ margin: "0 4px 0 0" }} />
                      ไปขั้นบันทึก / ยืนยันนัดหมาย
                    </Button>
                  )}
                </>
              )}

              {/* ----- 9.4 Step 2: Cards (ยก snippet จาก ServicePayment.tsx) ----- */}
              {step === 2 && (
                <>
                  {/* กลับไปแก้สถานที่/วัน/เวลา */}
                  <div
                    style={{
                      marginBottom: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      cursor: "pointer",
                    }}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      // ตั้งใจไม่ให้ reset r่องรอย → effect ไม่ยัดกลับมา step 2 เอง
                      setStep(1);
                    }}
                  >
                    <Icon name="arrow left" size="small" style={{ color: PRIMARY_COLOR }} />
                    <span style={{ fontSize: "11px", fontWeight: 600, color: PRIMARY_COLOR }}>
                      แก้สถานที่ / วัน / เวลา
                    </span>
                  </div>
                  <CardAddressInfo
                    slot={locState.slot}
                    locationState={locState}
                    onEvent={props.onEvent}
                  />
                  <CardSymptomInfo
                    answers={locState.basicSymptomInfo?.answers}
                    note={locState.appointmentOrderNote}
                    editable={!isReadOnly && !locState.isPostponeApp}
                    onEdit={handleEditInfo}
                  />
                  {canSelectPackages && (
                    <CardAppointmentPackages
                      onEvent={props.onEvent}
                      selectedPackages={locState.appointmentPackages || []}
                      onChange={handleChangePackages}
                    />
                  )}
                  {/* ----- 9.5 แจ้งข้อผิดพลาด + ยืนยันสร้างนัดหมาย ----- */}
                  <div style={{ marginTop: "12px" }}>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#333333",
                        marginBottom: "4px",
                      }}
                    >
                      บันทึกการนัดหมาย
                    </div>
                    <textarea
                      value={orderNote}
                      rows={2}
                      placeholder="เช่น ตรวจติดตามผล / ขอซ้ำเวลานี้เพราะคนละบริการ"
                      onChange={(e) => setOrderNote(e.target.value)}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        fontSize: "11px",
                        padding: "6px 8px",
                        border: "1px solid #ECECEC",
                        borderRadius: "6px",
                        outline: "none",
                        resize: "vertical",
                      }}
                    />
                    <div style={{ fontSize: "10px", color: "#999999", marginTop: "3px" }}>
                      ถ้าผู้รับบริการมีนัดอื่นภายใน 3 ชั่วโมง ระบบจะบังคับให้กรอกบันทึกนี้
                    </div>
                  </div>

                  {formError && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: "10px",
                        color: "#9c3030",
                        background: "#FDECEA",
                        border: "1px solid #F5C6C6",
                        borderRadius: "5px",
                        padding: "6px 8px",
                        marginTop: "10px",
                      }}
                    >
                      <span>
                        <Icon name="warning" style={{ margin: "0 4px 0 0" }} />
                        {typeof formError === "string"
                          ? formError
                          : postponeAppointment
                            ? "เลื่อนนัดหมายไม่สำเร็จ"
                            : "สร้างนัดหมายไม่สำเร็จ"}
                      </span>
                      <Icon
                        name="close"
                        link
                        style={{ margin: 0, color: "#9c3030" }}
                        onClick={closeFormError}
                      />
                    </div>
                  )}

                  <div style={{ marginTop: "12px" }}>
                    <Button
                      fluid
                      primary
                      loading={isSubmitting}
                      disabled={!selectedSlot}
                      style={{
                        background: PRIMARY_COLOR,
                        borderColor: PRIMARY_COLOR,
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 600,
                        padding: "9px 0",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSubmitAppointment();
                      }}
                    >
                      <Icon name="calendar check outline" style={{ margin: "0 4px 0 0" }} />
                      {postponeAppointment ? "ยืนยันเลื่อนนัดหมาย" : "ยืนยันนัดหมาย"}
                    </Button>

                    <div
                      style={{
                        fontSize: "10px",
                        color: "#777777",
                        marginTop: "5px",
                        textAlign: "center",
                      }}
                    >
                      {postponeAppointment
                        ? "นัดเดิมจะถูกย้ายไปวัน/เวลาที่เลือก และกลับสู่สถานะ รอยืนยัน พร้อมแจ้งผู้รับบริการ"
                        : "นัดใหม่จะอยู่สถานะ รอยืนยัน และส่งข้อความแจ้งผู้รับบริการ"}
                    </div>
                  </div>
                </>
              )}
            </Segment>
          )}

          {/* ---------- รายการนัดหมาย ---------- */}

          {/* กำลังโหลด / ยังไม่มีข้อมูล → ขึ้น loading กันรายการของคนเก่าค้าง */}
          {isLoadingAppointments && (
            <Segment
              style={{
                padding: "12px 9px",
                margin: "5px 0 0 0",
                border: `solid 1px ${PRIMARY_COLOR}`,
                borderRadius: "5px",
                backgroundColor: "#FFF8FA",
                display: "flex",
                alignItems: "center",
                gap: "7px",
              }}
            >
              <Icon name="circle notched" loading style={{ color: PRIMARY_COLOR, margin: 0 }} />
              <span style={{ fontSize: "11px", color: "#777777", flex: 1 }}>
                {appointmentLoadStalled
                  ? "โหลดรายการนัดหมายใช้เวลานานผิดปกติ"
                  : "กำลังโหลดรายการนัดหมาย..."}
              </span>
              {appointmentLoadStalled && (
                <Button
                  size="mini"
                  basic
                  style={{
                    color: PRIMARY_COLOR,
                    borderColor: PRIMARY_COLOR,
                    fontSize: "10px",
                    padding: "4px 8px",
                    margin: 0,
                    borderRadius: "5px",
                  }}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    reloadAppointments();
                  }}
                >
                  <Icon name="refresh" style={{ margin: "0 3px 0 0" }} />
                  โหลดใหม่
                </Button>
              )}
            </Segment>
          )}

          {confirmError && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "10px",
                color: "#9c3030",
                background: "#FDECEA",
                border: "1px solid #F5C6C6",
                borderRadius: "5px",
                padding: "6px 8px",
                margin: "5px 0 0 0",
              }}
            >
              <span>
                <Icon name="warning" style={{ margin: "0 4px 0 0" }} />
                {typeof confirmError === "string" ? confirmError : "ยืนยันนัดหมายไม่สำเร็จ"}
              </span>
              <Icon
                name="close"
                link
                style={{ margin: 0, color: "#9c3030" }}
                onClick={clearConfirmError}
              />
            </div>
          )}

          {!isLoadingAppointments && filteredAppointments.length === 0 && (
            <Segment
              style={{
                padding: "12px 9px",
                margin: "5px 0 0 0",
                border: `solid 1px ${PRIMARY_COLOR}`,
                borderRadius: "5px",
                backgroundColor: "#FFFFFF",
                fontSize: "11px",
                color: "#999999",
              }}
            >
              ยังไม่มีรายการนัดหมาย
            </Segment>
          )}

          {/* แจ้งเตือนจากการซิงก์ Google Calendar */}
          {calendarNotice && (
            <div
              style={{
                fontSize: "10px",
                color: "#8a6d3b",
                background: "#fcf8e3",
                border: "1px solid #faebcc",
                borderRadius: "4px",
                padding: "5px 7px",
                margin: "5px 0",
              }}
            >
              <Icon name="info" style={{ margin: "0 4px 0 0" }} />
              {calendarNotice}
            </div>
          )}

          {filteredAppointments.map((appointment, appointmentIndex) => (
            <Segment
              key={appointment.id}
              style={{
                ...styles.listItem,
                marginTop: appointmentIndex === 0 && !showAddForm ? 0 : "5px",
                borderTop:
                  appointmentIndex === 0 && !showAddForm ? "none" : `solid 1px ${PRIMARY_COLOR}`,
                borderRadius: appointmentIndex === 0 && !showAddForm ? "0 0 5px 5px" : "5px",
              }}
            >
              <Label
                style={{
                  position: "absolute",
                  top: "8px",
                  right: -1,
                  fontSize: "10px",
                  borderRadius: "5px 0 0 5px",
                  ...getStatusDisplay(appointment.status).style,
                }}
              >
                {getStatusDisplay(appointment.status).text}
              </Label>
              <div style={{ fontWeight: 500, fontSize: "11px", marginBottom: "8px" }}>
                <strong>{appointment.division_name}</strong>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "5px",
                  fontSize: "11px",
                }}
              >
                <span style={{ marginRight: "5px" }}>วันเวลาที่นัดหมาย : </span>
                <Icon name="calendar" style={{ color: PRIMARY_COLOR, margin: "-11px 5px 0 0" }} />
                <span>{splitDateTime(appointment.estimated_at).date || "-"}</span>
                <Icon name="clock" style={{ color: PRIMARY_COLOR, margin: "-11px 5px 0 10px" }} />
                <span>{splitDateTime(appointment.estimated_at).time || "-"}</span>
              </div>
              <div
                style={{
                  marginTop: "5px",
                  fontSize: "11px",
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>วัตถุประสงค์ในการนัดหมาย </span>
                <span
                  style={{
                    marginLeft: "7px",
                    marginRight: "5px",
                    wordBreak: "break-word",
                  }}
                >
                  :
                </span>
                <span>{appointment.order_note || "-"}</span>
              </div>

              {/* Footer: ซิงก์ Google Calendar / แก้ไข / ลบ */}
              <div
                style={{
                  marginTop: "8px",
                  paddingTop: "6px",
                  borderTop: "1px dashed #F5CAD5",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {calendarEventMap[appointment.id]?.synced &&
                calendarEventMap[appointment.id]?.html_link ? (
                  <Button
                    size="mini"
                    basic
                    primary
                    title={calendarEventMap[appointment.id]?.event_id}
                    style={{
                      borderRadius: "4px",
                      padding: "4px 8px",
                      fontSize: "10px",
                      margin: 0,
                    }}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      window.open(
                        calendarEventMap[appointment.id].html_link!,
                        "_blank",
                        "noopener",
                      );
                    }}
                  >
                    <Icon name="calendar check outline" style={{ margin: "0 3px 0 0" }} />
                    บน Google
                  </Button>
                ) : (
                  <Button
                    size="mini"
                    basic
                    loading={!!calendarBusy[appointment.id]}
                    title={
                      calendarEventMap[appointment.id]?.error ||
                      "ซิงก์นัดนี้ขึ้นปฏิทินของแผนก และส่งคำเชิญไปหาผู้รับบริการ"
                    }
                    style={{
                      color: "#5DBCD2",
                      borderColor: "#5DBCD2",
                      boxShadow: "0 0 0 1px #5DBCD2 inset",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      fontSize: "10px",
                      margin: 0,
                      backgroundColor: "transparent",
                    }}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      syncAppointmentToCalendar(appointment);
                    }}
                  >
                    <Icon name="calendar plus outline" style={{ margin: "0 3px 0 0" }} />
                    ซิงก์
                  </Button>
                )}
                {canManageAppointment(appointment) && (
                  <>
                    {canConfirmAppointment(appointment) && (
                      <Button
                        size="mini"
                        basic
                        title="ยืนยันนัดหมายนี้ (สถานะจะเป็น ยืนยันแล้ว)"
                        loading={
                          confirmingAppointment?.id === appointment.id &&
                          !!props.loadingStatus?.[ACTION.loadConfirmApp]
                        }
                        style={{
                          color: "#2E7D32",
                          borderColor: "#2E7D32",
                          boxShadow: "0 0 0 1px #2E7D32 inset",
                          borderRadius: "4px",
                          padding: "4px 8px",
                          fontSize: "10px",
                          fontWeight: 500,
                          margin: 0,
                          backgroundColor: "transparent",
                        }}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          handleConfirmAppointment(appointment);
                        }}
                      >
                        <Icon name="check" style={{ margin: "0 3px 0 0" }} />
                        ยืนยันนัด
                      </Button>
                    )}
                    <Button
                      size="mini"
                      basic
                      title="เลือกวัน/เวลาใหม่ แล้วกดยืนยันเลื่อนนัดหมาย"
                      style={{
                        color: "#f2711c",
                        borderColor: "#f2711c",
                        boxShadow: "0 0 0 1px #f2711c inset",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        fontSize: "10px",
                        fontWeight: 500,
                        margin: 0,
                        backgroundColor:
                          postponeAppointment?.id === appointment.id ? "#FFF0F3" : "transparent",
                      }}
                      onClick={(e: any) => {
                        e.stopPropagation();
                        // use caller handler when provided (same as before)
                        if (props.onEditAppointment) {
                          props.onEditAppointment(appointment);
                          return;
                        }
                        handleStartPostpone(appointment);
                      }}
                    >
                      <Icon name="edit" style={{ margin: "0 3px 0 0" }} />
                      เลื่อนนัด
                    </Button>
                    <Button
                      size="mini"
                      basic
                      style={{
                        color: "#DB2828",
                        borderColor: "#DB2828",
                        boxShadow: "0 0 0 1px #DB2828 inset",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        fontSize: "10px",
                        fontWeight: 500,
                        margin: 0,
                        backgroundColor:
                          cancelAppointment?.id === appointment.id ? "#FDECEA" : "transparent",
                      }}
                      onClick={(e: any) => {
                        e.stopPropagation();
                        if (props.onDeleteAppointment) {
                          props.onDeleteAppointment(appointment);
                          return;
                        }
                        handleRequestCancel(appointment);
                      }}
                    >
                      <Icon name="ban" style={{ margin: "0 3px 0 0" }} />
                      ยกเลิกนัด
                    </Button>
                  </>
                )}
              </div>

              {/* ----- แผงยืนยันยกเลิก (main: หน้า /cancel-appointment/ + ช่อง note) ----- */}
              {cancelAppointment?.id === appointment.id && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "8px",
                    border: "1px solid #F5C6C6",
                    borderRadius: "6px",
                    background: "#FDECEA",
                  }}
                >
                  <div style={{ fontSize: "11px", fontWeight: 600, color: "#9c3030" }}>
                    <Icon name="warning" style={{ margin: "0 4px 0 0" }} />
                    ยืนยันยกเลิกนัดหมายนี้
                  </div>
                  <textarea
                    value={cancelNote}
                    rows={2}
                    placeholder="เหตุผลที่ยกเลิก (เช่น ผู้รับบริการแจ้งไม่ว่าง) — บันทึกส่งไปถึงผู้รับบริการ"
                    onChange={(e) => setCancelNote(e.target.value)}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      fontSize: "11px",
                      padding: "6px 8px",
                      marginTop: "6px",
                      border: "1px solid #E8B4B4",
                      borderRadius: "6px",
                      outline: "none",
                      resize: "vertical",
                    }}
                  />
                  {cancelError && (
                    <div style={{ fontSize: "10px", color: "#9c3030", marginTop: "5px" }}>
                      <Icon name="warning" style={{ margin: "0 4px 0 0" }} />
                      {typeof cancelError === "string" ? cancelError : "ยกเลิกนัดหมายไม่สำเร็จ"}
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      justifyContent: "flex-end",
                      marginTop: "7px",
                    }}
                  >
                    <Button
                      size="mini"
                      basic
                      style={{
                        fontSize: "10px",
                        padding: "4px 8px",
                        margin: 0,
                        borderRadius: "5px",
                      }}
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setCancelAppointment(null);
                        setCancelNote("");
                      }}
                    >
                      ไม่ยกเลิก
                    </Button>
                    <Button
                      size="mini"
                      primary
                      loading={!!props.loadingStatus?.[ACTION.loadCancelApp]}
                      style={{
                        background: "#DB2828",
                        borderColor: "#DB2828",
                        fontSize: "10px",
                        fontWeight: 600,
                        padding: "4px 10px",
                        margin: 0,
                        borderRadius: "5px",
                      }}
                      onClick={(e: any) => {
                        e.stopPropagation();
                        handleConfirmCancel(appointment);
                      }}
                    >
                      <Icon name="ban" />
                      ยกเลิกนัดหมาย
                    </Button>
                  </div>
                </div>
              )}
            </Segment>
          ))}
        </>
      )}
    </Segment.Group>
  );
};

export default BookingSection;
